/**
 * Lightweight analytics wrapper.
 *
 * - Dev: logs to console.
 * - Prod: pushes to window.dataLayer (GTM / GA4 compatible).
 *
 * Tracked events:
 *   landing_view              — page mounted
 *   hero_cta_click            — waitlist button clicked from hero
 *   waitlist_submit_success   — email successfully saved
 *   waitlist_submit_error     — submit failed (validation or API)
 *   privacy_link_click        — footer privacy link clicked
 *   sample_question_start     — interactive demo "Try" button clicked
 *   blog_index_view           — /blog list page mounted
 *   blog_post_view            — /blog/:slug article page mounted
 */

export type AnalyticsEvent =
  | 'landing_view'
  | 'hero_cta_click'
  | 'waitlist_submit_success'
  | 'waitlist_submit_error'
  | 'privacy_link_click'
  | 'sample_question_start'
  | 'blog_index_view'
  | 'blog_post_view';

type EventPayload = Record<string, string | number | boolean | undefined>;

export const track = (event: AnalyticsEvent, payload?: EventPayload): void => {
  if (typeof window === 'undefined') return;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[analytics]', event, payload ?? '');
    return;
  }

  // Push to global dataLayer — compatible with GTM, GA4, and future tag managers.
  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer ??= [];
  w.dataLayer.push({ event, timestamp: Date.now(), ...payload });
};
